// src/pages/admin/ContractManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import contractService from '../../services/contractService';
import { useToast } from '../../hooks/useToast';
import { extractApiList } from '../../utils/responseAdapters';
import Button from '../../components/common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileContract, faCheck, faXmark, faBan, faClock,
  faCircleCheck, faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_META = {
  pending:    { label: 'Pending',    color: '#f59e0b' },
  active:     { label: 'Active',     color: '#10b981' },
  terminated: { label: 'Terminated', color: '#ef4444' },
  expired:    { label: 'Expired',    color: '#6b7280' },
  rejected:   { label: 'Rejected',   color: '#ef4444' },
  cancelled:  { label: 'Cancelled',  color: '#6b7280' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, color: '#6b7280' };
  return (
    <span style={{
      background: meta.color + '22', color: meta.color,
      padding: '3px 10px', borderRadius: 20, fontWeight: 600, fontSize: 13,
    }}>
      {meta.label}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function daysRemaining(endDate) {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / 86400000);
}

// ─── Pending Contract Card ────────────────────────────────────────────────────

function PendingCard({ contract, onApprove, onReject }) {
  const [acting, setActing] = useState(false);

  const handle = async (action) => {
    setActing(true);
    await action();
    setActing(false);
  };

  return (
    <div className="application-card">
      <div className="card-header">
        <div>
          <strong style={{ fontSize: 16 }}>
            {contract.artist_info?.name || contract.artist_id}
          </strong>
          <div style={{ fontSize: 13, color: '#888' }}>{contract.artist_info?.email}</div>
        </div>
        <StatusBadge status="pending" />
      </div>
      <div style={{ display: 'flex', gap: 20, margin: '12px 0', fontSize: 13, color: '#555' }}>
        <span>Duration: <strong>{contract.duration_months} month{contract.duration_months !== 1 ? 's' : ''}</strong></span>
        <span>Requested: <strong>{formatDate(contract.created_at)}</strong></span>
      </div>
      <div style={{ fontSize: 12, color: '#aaa', marginBottom: 14 }}>ID: {contract.contract_id}</div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Button
          variant="secondary"
          disabled={acting}
          onClick={() => handle(onApprove)}
        >
          <FontAwesomeIcon icon={faCheck} style={{ marginRight: 6, color: '#10b981' }} />
          Approve
        </Button>
        <Button
          variant="tertiary"
          disabled={acting}
          onClick={() => handle(onReject)}
        >
          <FontAwesomeIcon icon={faXmark} style={{ marginRight: 6, color: '#ef4444' }} />
          Reject
        </Button>
      </div>
    </div>
  );
}

// ─── Active Contract Card ─────────────────────────────────────────────────────

function ActiveCard({ contract, onTerminate }) {
  const days = daysRemaining(contract.end_date);
  const expiring = days !== null && days <= 30;

  return (
    <div className="application-card">
      {expiring && (
        <div style={{
          background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
          padding: '8px 14px', marginBottom: 12, fontSize: 13, color: '#92400e',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
          Expires in <strong>{days} day{days !== 1 ? 's' : ''}</strong>
        </div>
      )}
      <div className="card-header">
        <div>
          <strong style={{ fontSize: 16 }}>
            {contract.artist_info?.name || contract.artist_id}
          </strong>
          <div style={{ fontSize: 13, color: '#888' }}>{contract.artist_info?.email}</div>
        </div>
        <StatusBadge status="active" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, margin: '12px 0', fontSize: 13 }}>
        {[
          ['Duration', `${contract.duration_months}mo`],
          ['Start', formatDate(contract.start_date)],
          ['End', formatDate(contract.end_date)],
        ].map(([k, v]) => (
          <div key={k}>
            <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>{k}</div>
            <div style={{ fontWeight: 600 }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12, color: '#aaa', marginBottom: 14 }}>ID: {contract.contract_id}</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="tertiary" onClick={onTerminate}>
          <FontAwesomeIcon icon={faBan} style={{ marginRight: 6 }} />
          Terminate
        </Button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ManagerContractManagement() {
  const { showToast } = useToast();

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [confirmTerminate, setConfirmTerminate] = useState(null);

  const pending  = contracts.filter(c => c.status === 'pending');
  const active   = contracts.filter(c => c.status === 'active');
  const history  = contracts.filter(c => !['pending', 'active'].includes(c.status));

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await contractService.getManagerContracts();
      const data = extractApiList(res?.data);
      setContracts(Array.isArray(data) ? data : []);
    } catch {
      showToast('Failed to load contracts', 500);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const handleApprove = async (contractId) => {
    try {
      await contractService.approveContract(contractId);
      showToast('Contract approved', 200);
      await fetchContracts();
    } catch {
      showToast('Failed to approve contract', 500);
    }
  };

  const handleReject = async (contractId) => {
    try {
      await contractService.rejectContract(contractId);
      showToast('Contract rejected', 200);
      await fetchContracts();
    } catch {
      showToast('Failed to reject contract', 500);
    }
  };

  const handleTerminate = async (contractId) => {
    try {
      await contractService.terminateContractManager(contractId);
      showToast('Contract terminated', 200);
      setConfirmTerminate(null);
      await fetchContracts();
    } catch {
      showToast('Failed to terminate contract', 500);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading contracts…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div style={{ padding: '24px 20px', maxWidth: 860 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <FontAwesomeIcon icon={faFileContract} style={{ fontSize: 22, color: '#2d8cf0' }} />
          <h2 style={{ margin: 0 }}>Contract Management</h2>
          {pending.length > 0 && (
            <span style={{
              background: '#ef4444', color: '#fff', borderRadius: 20,
              padding: '2px 10px', fontSize: 13, fontWeight: 700,
            }}>
              {pending.length} pending
            </span>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '2px solid #eee' }}>
          {[
            { key: 'pending', label: `Pending Requests (${pending.length})` },
            { key: 'active',  label: `Active Contracts (${active.length})` },
            { key: 'history', label: `History (${history.length})` },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '8px 16px', border: 'none', background: 'none',
                borderBottom: activeTab === tab.key ? '2px solid #2d8cf0' : '2px solid transparent',
                color: activeTab === tab.key ? '#2d8cf0' : '#555',
                fontWeight: activeTab === tab.key ? 700 : 400,
                cursor: 'pointer', marginBottom: -2,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Pending tab */}
        {activeTab === 'pending' && (
          <div>
            {pending.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#888', padding: 40 }}>No pending contract requests.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {pending.map(c => (
                  <PendingCard
                    key={c.contract_id}
                    contract={c}
                    onApprove={() => handleApprove(c.contract_id)}
                    onReject={() => handleReject(c.contract_id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Active tab */}
        {activeTab === 'active' && (
          <div>
            {active.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#888', padding: 40 }}>No active contracts.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {active.map(c => (
                  <ActiveCard
                    key={c.contract_id}
                    contract={c}
                    onTerminate={() => setConfirmTerminate(c.contract_id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* History tab */}
        {activeTab === 'history' && (
          <div>
            {history.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#888', padding: 40 }}>No contract history yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {history.map(c => (
                  <div key={c.contract_id} className="application-card">
                    <div className="card-header">
                      <div>
                        <strong>{c.artist_info?.name || c.artist_id}</strong>
                        <div style={{ fontSize: 13, color: '#888' }}>{c.artist_info?.email}</div>
                      </div>
                      <StatusBadge status={c.status} />
                    </div>
                    <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 13, color: '#555' }}>
                      <span>Duration: <strong>{c.duration_months}mo</strong></span>
                      <span>From: <strong>{formatDate(c.start_date)}</strong></span>
                      <span>To: <strong>{formatDate(c.end_date)}</strong></span>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: '#aaa' }}>ID: {c.contract_id}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Terminate confirm dialog */}
      {confirmTerminate && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 420, width: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Terminate Contract?</h3>
            <p style={{ color: '#555' }}>
              The artist will no longer have a managed relationship with you. Their pending tokenization applications will remain in the system but no new ones can be submitted without a new contract.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
              <Button variant="tertiary" onClick={() => setConfirmTerminate(null)}>Cancel</Button>
              <Button variant="secondary" onClick={() => handleTerminate(confirmTerminate)}>
                Yes, Terminate
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
